import React, { useEffect, useMemo, useState, useCallback } from "react";
import { doc, onSnapshot, collection } from "firebase/firestore";
import RequestReplyTab from "./RequestReplyTab";
import { FlightRequest, FlightDetail } from "./Types";
import SearchBar from "./SearchBar";
import SortDropdown from "./SortDropdown";
import {
  getFlightRequests,
  lockUnlockResponse,
  getAnsweredRequest,
} from "apis/requestServices";
import { useGlobalLoader } from "contexts/GlobalLoaderContext";
import { auth, firestore } from "config/firebaseConfig";
import DefaultAirLogo from "../../assets/images/logo.svg";
import { useToastStore } from "store/toastStore";
import { useCreditStore } from "store/creditStore";
import Pagination from "../../components/Pagination";

interface LockStatus {
  requestDetailId: number;
  isLocked: boolean;
}

export const OpenRequest: React.FC = () => {
  const [selectedFlight, setSelectedFlight] = useState<FlightRequest | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<string>("DepartureTime");
  const { showLoader, hideLoader } = useGlobalLoader();
  const [flightRequests, setFlightRequests] = useState<FlightRequest[]>([]);
  const [selectedFlightDetail, setSelectedFlightDetail] =
    useState<FlightDetail | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [flightResponse, setFlightResponse] = useState<any>();
  const [lockStatuses, setLockStatuses] = useState<LockStatus[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [pageSize, setPageSize] = useState(10);
  const status = "open";
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Debounce function
  const debounce = <F extends (...args: any[]) => any>(
    func: F,
    wait: number
  ) => {
    let timeout: NodeJS.Timeout;
    return (...args: Parameters<F>): Promise<ReturnType<F>> => {
      return new Promise((resolve) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => resolve(func(...args)), wait);
      });
    };
  };

  // Map display names to API sortBy values
  const mapSortByToApiValue = (displayName: string): string => {
    switch (displayName) {
      case "Departure Time":
        return "DepartureTime";
      case "Flight Number":
        return "FlightNumber";
      case "Route":
        return "Route";
      default:
        return "DepartureTime";
    }
  };

  // Fetch flight requests
  const fetchFlightRequests = useCallback(
    debounce(async () => {
      showLoader();
      try {
        const response = await getFlightRequests({
          pageNumber: currentPage,
          pageSize,
          status,
          sortBy: sortBy,
          searchText: debouncedSearchQuery.trim() || undefined,
        });
        setError(null);
        setFlightResponse(response);
        const requests = response.items.map((r: any) => ({
          ...r,
          departureDate: `${new Date(r.departure).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric", timeZone: "UTC" })} - ${new Date(r.departure).toLocaleDateString("en-US", { weekday: "short", timeZone: "UTC" })}`,
          departureTime: r.departure
            ? r.departure.split("T")[1].slice(0, 5)
            : "",

          arrivalTime: r.arrival
            ? r.arrival.split("T")[1].slice(0, 5)
            : "",
          duration: r.elptim
          ? `${parseInt(r.elptim.slice(0, -2))}h ${parseInt(
              r.elptim.slice(-2)
            )}m`
          : "",
        }));
        setFlightRequests(requests);
      } catch (error) {
        console.error("Error:", error);
        setFlightRequests([]);
      } finally {
        setIsRefreshing(false);
        setTimeout(() => hideLoader(), 500);
      }
    }, 500),
    [currentPage, reloadTrigger, sortBy, pageSize, debouncedSearchQuery]
  );

  // Manual refresh — re-fetch the list (the open-request count badge derives
  // from the same response's totalCount, so it refreshes with it). Also
  // clears the search box and returns to page 1, since a "refresh" should
  // reset the view rather than silently re-run whatever filter was active.
  const handleRefresh = () => {
    setIsRefreshing(true);
    setSearchQuery("");
    setDebouncedSearchQuery("");
    setCurrentPage(1);
    setReloadTrigger((prev) => prev + 1);
  };

  // Set up real-time listener for lock statuses
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      setError("User not authenticated");
      setLockStatuses(
        flightRequests.map((r) => ({
          requestDetailId: r.requestDetailId,
          isLocked: false,
        }))
      );
      return;
    }

    const uid = user.uid;
    const requestsCollectionRef = collection(
      firestore,
      "userRequests",
      uid,
      "requests"
    );

    // Set up the real-time listener
    const unsubscribe = onSnapshot(
      requestsCollectionRef,
      (snapshot) => {
        const updatedStatuses: LockStatus[] = flightRequests.map((r) => {
          const doc = snapshot.docs.find(
            (d) => d.id === String(r.requestDetailId)
          );
          if (doc && doc.exists()) {
            const data = doc.data();
            const isLocked = data.status === "In Progress";
            if (isLocked) {
              setExpiresAt(data.expiresAt);
            }
            return { requestDetailId: r.requestDetailId, isLocked };
          }
          return { requestDetailId: r.requestDetailId, isLocked: false };
        });
        setLockStatuses(updatedStatuses);
      },
      (error) => {
        console.error("Error in Firestore listener:", error);
        setError("Failed to fetch lock statuses");
      }
    );

    // Clean up the listener on component unmount
    return () => unsubscribe();
  }, [flightRequests]);

  // Auto-close modal when expiresAt is reached
  useEffect(() => {
    if (!isModalOpen || !expiresAt) return;

    const checkExpiration = () => {
      const currentTime = new Date().getTime();
      const expirationTime = new Date(expiresAt).getTime();
      if (currentTime >= expirationTime) {
        handleCloseModal();
      }
    };

    const intervalId = setInterval(checkExpiration, 1000); // Check every second
    checkExpiration(); // Immediate check in case already expired

    return () => clearInterval(intervalId); // Clean up interval
  }, [isModalOpen, expiresAt]);

  // Debounce the search input so we refetch once the user pauses typing,
  // instead of firing a request on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery), 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset currentPage to 1 when sortBy or the search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [sortBy, debouncedSearchQuery]);

  // Fetch flight requests
  useEffect(() => {
    fetchFlightRequests();
  }, [fetchFlightRequests]);

  const handlePageChange = (newPage: number) => {
    if (
      newPage >= 1 &&
      newPage <= Math.ceil(flightResponse?.totalCount / pageSize)
    ) {
      setCurrentPage(newPage);
    }
  };

  // Change rows-per-page and jump back to the first page.
  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  const handleCardClick = async (flight: FlightRequest) => {
    const lockStatus = lockStatuses.find(
      (s) => s.requestDetailId === flight.requestDetailId
    );
    if (lockStatus?.isLocked) return;

    // Acquire the lock and confirm the request hasn't already been answered
    // (the row we clicked may be stale — e.g. someone else answered it and
    // released the lock before this list was refreshed) before showing the
    // form, so the user never sees a reply form open just to be pulled away.
    showLoader();
    try {
      await lockUnlockResponse(Number(flight.requestDetailId), false);
    } catch (error: any) {
      hideLoader();
      const message =
        error?.response?.status === 423
          ? "This request is currently being answered by someone else."
          : "Failed to open this request. Please try again.";
      useToastStore.getState().showToast(message, "error", 3000);
      console.error("Error locking flight request:", error);
      return;
    }

    try {
      await getAnsweredRequest(Number(flight.requestDetailId));
      // No error means an answer already exists for this request.
      lockUnlockResponse(Number(flight.requestDetailId), true).catch(() => {});
      hideLoader();
      setFlightRequests((prev) =>
        prev.filter((r) => r.requestDetailId !== flight.requestDetailId)
      );
      setFlightResponse((prev: any) =>
        prev ? { ...prev, totalCount: Math.max(0, prev.totalCount - 1) } : prev
      );
      useToastStore
        .getState()
        .showToast(
          "This request has already been answered by someone else.",
          "info",
          3000
        );
      return;
    } catch (checkError) {
      // No existing answer found — safe to proceed with the reply form.
    }
    hideLoader();

    const flightDetail: FlightDetail = {
      logoImg: flight.airlineLogo,
      airlineId: flight.airlineId,
      airlineName: flight.airlineName,
      flightNumber: flight.flightNumber,
      departureAirport: flight.sourceCode,
      arrivalAirport: flight.destinationCode,
      departureTime: flight.departureTime,
      arrivalTime: flight.arrivalTime,
      flightDuration: flight.duration,
      requestDetailId: flight.requestDetailId,
      flightDate: new Date(flight.flightDate)
        .toLocaleDateString("en-US", {
          day: "numeric",
          month: "short",
          weekday: "short",
          timeZone: "UTC",
        })
        .toUpperCase()
        .replace(/^(\w+),\s(\w+)\s(\d+)$/, "$2 $3 - $1"),
    };
    setSelectedFlight(flight);
    setSelectedFlightDetail(flightDetail);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    const detailId = selectedFlightDetail?.requestDetailId;
    // Close immediately; release the lock in the background.
    setIsModalOpen(false);
    setSelectedFlight(null);
    setSelectedFlightDetail(null);
    if (detailId) {
      lockUnlockResponse(Number(detailId), true).catch((error) => {
        console.error("Error unlocking flight request:", error);
      });
    }
  };

  // Called after a successful answer: drop the request from the open list
  // immediately, refresh the earned-token balance, then close (unlock in bg).
  const handleSubmit = () => {
    const answeredId = selectedFlightDetail?.requestDetailId;
    if (answeredId != null) {
      setFlightRequests((prev) =>
        prev.filter((r) => r.requestDetailId !== answeredId)
      );
      // Keep the header badge (derived from totalCount) in sync with the
      // optimistic row removal above.
      setFlightResponse((prev: any) =>
        prev ? { ...prev, totalCount: Math.max(0, prev.totalCount - 1) } : prev
      );
    }
    // Answering earns tokens — pull the new balance for the sidebar.
    useCreditStore.getState().refresh();
    handleCloseModal();
  };

  const filteredFlights = useMemo(() => {
    if (!Array.isArray(flightRequests)) return [];
    // Search and sorting are both applied server-side (searchText / sortBy),
    // so filtering here would only narrow the current page.
    return flightRequests.filter((flight) => flight.status === "Open");
  }, [flightRequests]);

  return (
    <section className="mt-6 ml-2 p-4 bg-white rounded-xl req-section">
      {error && <div className="text-red-500 text-center mb-4">{error}</div>}
      <div className="flex flex-wrap gap-3 items-center justify-between p-2 mb-1 req-header">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-lg req-breadcrumb">
          <span className="text-[#6B7280] font-bold">Requests</span>
          <span className="text-[#9CA3AF]">&gt;</span>
          <span className="text-[#1F2937] font-medium">Open</span>
          <span className="flex items-center justify-center w-6 h-6 text-[10px] font-bold rounded-full bg-[#FBBF24] text-black">
            {flightResponse?.totalCount ?? 0}
          </span>
        </div>
        <div className="flex items-center gap-3 relative req-toolbar">
          {/* display:contents on desktop, a real flex item on phones — see
              .req-toolbar__search in index.css. */}
          <div className="req-toolbar__search contents">
            <SearchBar
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
            />
          </div>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="flex items-center justify-center w-10 h-10 bg-white border border-[#EDEEF1] rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-60 req-toolbar__btn"
            title="Refresh"
            aria-label="Refresh"
          >
            <i
              className={`st-reload text-[#1F2937] ${isRefreshing ? "animate-spin" : ""}`}
            ></i>
          </button>
          <SortDropdown
            currentSortBy={sortBy}
            onSortChange={(displayName) => {
              const apiValue = mapSortByToApiValue(displayName);
              setSortBy(apiValue);
            }}
          />
        </div>
      </div>
      {filteredFlights.length === 0 ? (
        <div className="flex justify-center items-center h-24 w-full text-[#6B7280]">
          <p>No flight requests found.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredFlights.map((flight: any, index) => {
            const lockStatus = lockStatuses.find(
              (s) => s.requestDetailId === flight.requestDetailId
            );
            const isLocked = lockStatus?.isLocked ?? false;
            return (
              <div
                key={index}
                // Desktop keeps the Tailwind classes below; the phone layout
                // lives in index.css under .req-card (assets/css/style.css
                // outranks Tailwind responsive variants — see the note there).
                className={`relative group font-manrope req-card flex items-center gap-4 px-4 py-[7px] overflow-x-auto bg-[#F8F8F8] border rounded-[6px] transition-all duration-200 ease-out ${
                  !isLocked
                    ? "cursor-pointer border-[#ECECED] hover:shadow-md hover:-translate-y-0.5"
                    : "cursor-not-allowed border-[#ECECED]"
                }`}
                onClick={() => handleCardClick(flight)}
                title={
                  isLocked
                    ? "This request is currently being answered by someone else."
                    : undefined
                }
              >
                {isLocked && (
                  <>
                    {/* Dim the card content while keeping the badge/tooltip crisp. */}
                    <div className="absolute inset-0 rounded-[6px] bg-white/55 z-[1] pointer-events-none" />
                    {/* Persistent "In use" badge + richer tooltip on hover. */}
                    <div className="absolute top-2 right-2 z-10 flex items-center gap-1 rounded-full bg-[#FDECEC] px-2.5 py-1 pointer-events-none">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className="w-3 h-3 text-[#DC4043]"
                      >
                        <path d="M12 1a5 5 0 0 0-5 5v3H6a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2h-1V6a5 5 0 0 0-5-5Zm3 8H9V6a3 3 0 0 1 6 0v3Z" />
                      </svg>
                      <span className="text-[11px] font-semibold text-[#DC4043] leading-none">
                        In use
                      </span>
                      <span className="pointer-events-none absolute top-full right-0 mt-1.5 hidden group-hover:block whitespace-nowrap rounded-md bg-[#1F2937] px-2.5 py-1.5 text-[11px] font-medium text-white shadow-lg">
                        This request is currently being answered by someone else.
                      </span>
                    </div>
                  </>
                )}
                {/* Airline */}
                <div className="req-card__airline flex items-center flex-1 min-w-0 gap-3">
                  <img
                    className="w-9 h-9 rounded-lg object-contain"
                    src={flight.airlineLogo || DefaultAirLogo}
                    alt={flight.airlineName?.toLowerCase() || "Airline"}
                    onError={(e) =>
                      (e.currentTarget.src = "/assets/logos/air-logo.png")
                    }
                  />
                  <div className="req-card__airline-text">
                    <p className="req-card__ellipsis text-black text-sm font-semibold whitespace-nowrap">{flight.airlineName}</p>
                    <p className="req-card__ellipsis text-[#737C8C] text-sm font-medium whitespace-nowrap">{flight.flightNumber}</p>
                  </div>
                </div>

                {/* Date — shares the first row with the airline block on
                    phones, so it uses a short month there and reserves room for
                    the "In use" badge when the request is locked. */}
                <div
                  className={`req-card__date flex items-center flex-1 min-w-0 ${
                    isLocked ? "req-card__date--locked" : ""
                  }`}
                >
                  <div className="flex flex-col gap-0.5">
                    <p className="text-[#737C8C] text-[11px] font-normal whitespace-nowrap">Date</p>
                    <p className="text-[#262626] text-sm font-medium whitespace-nowrap">
                      {(() => {
                        const date = new Date(flight?.flightDate);
                        const day = String(date.getUTCDate()).padStart(2, "0");
                        const year = date.getUTCFullYear();
                        const long = date.toLocaleString("en-US", { month: "long", timeZone: "UTC" });
                        const short = date.toLocaleString("en-US", { month: "short", timeZone: "UTC" });
                        return (
                          <>
                            <span className="req-card__date-short">{`${day} ${short}, ${year}`}</span>
                            <span className="req-card__date-long">{`${day} ${long}, ${year}`}</span>
                          </>
                        );
                      })()}
                    </p>
                  </div>
                </div>

                {/* Route — own row below sm */}
                <div className="req-card__route flex items-center w-2/5 flex-shrink-0 gap-2.5">
                  <div className="flex flex-col items-start gap-0.5 w-[54px] flex-shrink-0">
                    <p className="text-[#262626] text-base font-medium">{flight.sourceCode}</p>
                    <p className="text-[#737C8C] text-[11px] font-medium">{flight.departureTime}</p>
                  </div>

                  <div className="flex flex-col items-center w-full">
                    <div className="flex items-center w-full">
                      <div className="w-1.5 h-1.5 bg-[#C5CAD4] rounded-full flex-shrink-0"></div>
                      <div className="flex-1 border-b border-dashed border-[#9CA3AF] mx-1"></div>
                      <i className="st-flight-slim text-[#737C8C] text-xs mx-1"></i>
                      <div className="flex-1 border-b border-dashed border-[#9CA3AF] mx-1"></div>
                      <div className="w-1.5 h-1.5 bg-[#C5CAD4] rounded-full flex-shrink-0"></div>
                    </div>
                    <p className="text-[#737C8C] text-[11px] font-normal mt-0.5 whitespace-nowrap">{flight.duration}</p>
                  </div>

                  <div className="flex flex-col items-end gap-0.5 w-[54px] flex-shrink-0">
                    <p className="text-[#262626] text-base font-medium">{flight.destinationCode}</p>
                    <p className="text-[#737C8C] text-[11px] font-medium">{flight.arrivalTime}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {selectedFlightDetail && (
        <RequestReplyTab
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onSubmit={handleSubmit}
          logoImg={selectedFlightDetail.logoImg}
          airlineId={Number(selectedFlightDetail.airlineId)}
          airlineName={selectedFlightDetail.airlineName}
          flightNumber={selectedFlightDetail.flightNumber}
          flightDate={selectedFlightDetail.flightDate}
          departureAirport={selectedFlightDetail.departureAirport}
          arrivalAirport={selectedFlightDetail.arrivalAirport}
          departureTime={selectedFlightDetail.departureTime}
          arrivalTime={selectedFlightDetail.arrivalTime}
          flightDuration={selectedFlightDetail.flightDuration}
          requestDetailId={selectedFlightDetail.requestDetailId}
        />
      )}
      {(flightResponse?.totalCount ?? 0) > 0 && (
        <section>
          <Pagination
            currentPage={currentPage}
            totalCount={flightResponse?.totalCount ?? 0}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            showTotal
          />
        </section>
      )}
    </section>
  );
};

export default OpenRequest;
